import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Online/Offline Status überwachen
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      // Wenn offline, verwende gespeicherte Benutzerdaten
      if (!navigator.onLine) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Fehler beim Parsen der Benutzerdaten:', e);
        }
        setLoading(false);
        return;
      }
      
      // Wenn online, verifiziere das Token
      try {
        const response = await authApi.getMe();
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch (err) {
        // Nur bei expliziten Auth-Fehlern (401/403) ausloggen
        // Bei Netzwerkfehlern behalte die Session
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          // Netzwerkfehler - verwende gespeicherte Daten
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            console.error('Fehler beim Parsen der Benutzerdaten:', e);
          }
        }
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    // Login nur online möglich
    if (!navigator.onLine) {
      throw new Error('Login ist nur mit Internetverbindung möglich. Bitte versuchen Sie es später erneut.');
    }
    
    try {
      setError(null);
      const response = await authApi.login(email, password);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Anmeldung fehlgeschlagen';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (email, password, name, company = '') => {
    // Registrierung nur online möglich
    if (!navigator.onLine) {
      throw new Error('Registrierung ist nur mit Internetverbindung möglich.');
    }
    
    try {
      setError(null);
      const response = await authApi.register(email, password, name, company);
      
      // First user gets auto-approved and logged in
      if (response.data.access_token) {
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return { approved: true, user: userData };
      }
      
      // Other users need approval
      return { approved: false, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registrierung fehlgeschlagen';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      isOffline,
      login,
      register,
      logout,
      isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
